import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Alert,
  Divider
} from '@mui/material';
import {
  Send as SendIcon,
  Favorite as FavoriteIcon,
  Payment as PaymentIcon,
  Warning as WarningIcon,
  Email as EmailIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { notificationService } from '../services/NotificationService';
import { useAuth } from '../contexts/AuthContext';

const NotificationTestPanel: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);

  const handleSendNotification = async (type: string) => {
    if (!user) return;
    
    setLoading(type);
    try {
      switch (type) {
        case 'favorite_rented':
          await notificationService.notifyFavoriteRented(
            user.uid,
            'Robe de soirée élégante',
            '2024-12-15',
            '2024-12-18',
            'Marie Dubois'
          );
          break;
        
        case 'request_accepted':
          await notificationService.notifyRequestAccepted(
            user.uid,
            'Robe de cocktail moderne',
            '2024-12-20',
            '2024-12-22'
          );
          break;
        
        case 'request_rejected':
          await notificationService.notifyRequestRejected(
            user.uid,
            'Robe de gala luxueuse',
            '2024-12-25',
            '2024-12-27',
            'Dates non disponibles'
          );
          break;
        
        case 'return_reminder':
          await notificationService.scheduleReturnReminder(
            'booking_123',
            user.uid,
            '2024-12-14',
            'Robe de mariée vintage'
          );
          break;
        
        case 'payment_success':
          await notificationService.notifyPaymentSuccess(
            user.uid,
            'Robe de soirée dorée',
            120,
            'booking_456'
          );
          break;
        
        case 'damage_report':
          await notificationService.notifyDamageReport(
            user.uid,
            'Robe de cocktail',
            50,
            'Tache de vin sur le bas de la robe'
          );
          break;
        
        case 'request_sent':
          await notificationService.notifyRequestSent(
            'owner_123',
            'Sophie Martin',
            'Robe bohème',
            '2024-12-28',
            '2024-12-30'
          );
          break;
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la notification:', error);
    } finally {
      setLoading(null);
    }
  };

  if (!user) {
    return (
      <Alert severity="info">
        Connectez-vous pour tester les notifications
      </Alert>
    );
  }

  const testNotifications = [
    {
      type: 'favorite_rented',
      title: 'Robe favorite louée',
      description: 'Notifier qu\'une robe favorite a été réservée',
      icon: <FavoriteIcon sx={{ color: '#e91e63' }} />,
      color: '#e91e63'
    },
    {
      type: 'request_accepted',
      title: 'Demande acceptée',
      description: 'Notifier l\'acceptation d\'une demande de location',
      icon: <CheckCircleIcon sx={{ color: '#4caf50' }} />,
      color: '#4caf50'
    },
    {
      type: 'request_rejected',
      title: 'Demande refusée',
      description: 'Notifier le refus d\'une demande de location',
      icon: <CancelIcon sx={{ color: '#f44336' }} />,
      color: '#f44336'
    },
    {
      type: 'request_sent',
      title: 'Nouvelle demande',
      description: 'Notifier le propriétaire d\'une nouvelle demande',
      icon: <EmailIcon sx={{ color: '#2196f3' }} />,
      color: '#2196f3'
    },
    {
      type: 'return_reminder',
      title: 'Rappel de retour',
      description: 'Programmer un rappel de retour de robe',
      icon: <WarningIcon sx={{ color: '#ff9800' }} />,
      color: '#ff9800'
    },
    {
      type: 'payment_success',
      title: 'Paiement confirmé',
      description: 'Notifier un paiement réussi',
      icon: <PaymentIcon sx={{ color: '#4caf50' }} />,
      color: '#4caf50'
    },
    {
      type: 'damage_report',
      title: 'Signalement de dommage',
      description: 'Notifier un dommage sur une robe',
      icon: <WarningIcon sx={{ color: '#ff5722' }} />,
      color: '#ff5722'
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ color: '#a5816d', mb: 3 }}>
        🧪 Panel de test des notifications
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Ce panel permet de tester les différents types de notifications. 
          Les notifications sont actuellement simulées et affichées dans la console.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {testNotifications.map((notification) => (
          <Grid item xs={12} sm={6} md={4} key={notification.type}>
            <Card 
              sx={{ 
                height: '100%',
                border: `2px solid ${notification.color}20`,
                '&:hover': {
                  boxShadow: `0 4px 8px ${notification.color}30`
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {notification.icon}
                  <Typography variant="h6" sx={{ ml: 1, color: notification.color }}>
                    {notification.title}
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {notification.description}
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<SendIcon />}
                  onClick={() => handleSendNotification(notification.type)}
                  disabled={loading === notification.type}
                  sx={{
                    backgroundColor: notification.color,
                    '&:hover': {
                      backgroundColor: notification.color,
                      opacity: 0.9
                    },
                    '&:disabled': {
                      backgroundColor: notification.color,
                      opacity: 0.6
                    }
                  }}
                >
                  {loading === notification.type ? 'Envoi...' : 'Envoyer'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Alert severity="success">
          <Typography variant="body2">
            <strong>Instructions :</strong>
            <br />
            1. Cliquez sur "Envoyer" pour créer une notification
            <br />
            2. Vérifiez la console du navigateur pour voir les logs
            <br />
            3. Cliquez sur l'icône de notification dans la barre de navigation pour voir les notifications
            <br />
            4. Testez les paramètres de notification dans le menu des notifications
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};

export default NotificationTestPanel;
